import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { getFareDetails } from "../common/getFareDetails";
import { getLangKey } from "common/src/other/getLangKey";

const BookingReceiptPrint = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const settings = useSelector((state) => state.settingsdata.settings);
  const cartypes = useSelector((state) => state.cartypes.cars);
  const bookinglist = useSelector((state) => state.bookinglistdata.bookings);

  const [booking, setBooking] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null);
  const [senderProfile, setSenderProfile] = useState(null);
  const [driverCar, setDriverCar] = useState(null);

  // 1) tenta achar no redux (lista) primeiro
  useEffect(() => {
    const fromStore = bookinglist?.find((b) => String(b.id) === String(id));
    if (fromStore) {
      setBooking(fromStore);
      return;
    }

    // 2) fallback: buscar na API (ajuste o método conforme seu common/api)
    (async () => {
      try {
        // Troque para o método real do seu projeto, se for diferente:
        // ex: api.getBookingById(id) / api.getBooking(id) / api.fetchBooking(id)
        const fetched = await api.getBookingById?.(id);
        if (fetched) setBooking(fetched);
      } catch (e) {
        console.error("Erro ao buscar booking:", e);
      }
    })();
  }, [id, bookinglist]);

  const carTypeObj = useMemo(() => {
    if (!cartypes || !booking?.carType) return null;
    return cartypes.find((ct) => ct.name === booking.carType) || null;
  }, [cartypes, booking]);

  const fareDetails = useMemo(() => {
    if (!booking || !settings) return [];
    return getFareDetails({ booking, carTypeObj, settings, t, getLangKey });
  }, [booking, carTypeObj, settings, t]);

  // buscar perfis (driver e sender)
  useEffect(() => {
    if (!booking) return;

    (async () => {
      try {
        const d = booking?.driver ? await api.getUserById(booking.driver) : null;
        const s = booking?.customer ? await api.getUserById(booking.customer) : null;
        const car = booking?.driver ? await api.getDriverActiveCar(booking.driver, booking?.car_image) : null;
        setDriverProfile(d);
        setSenderProfile(s);
        setDriverCar(car);
      } catch (e) {
        console.error("Erro ao buscar perfis:", e);
      }
    })();
  }, [booking]);

  const html = useMemo(() => {
    if (!booking || !settings) return "";

    const locale = i18n.language || "pt-BR";

    const formatMoney = (value) => {
      const n = Number(value ?? 0);
      const decimals = Number(settings?.decimal ?? 2);
      return `${settings?.symbol ?? ""}${n.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}`;
    };

    const maskCPF = (cpf) => {
      if (!cpf) return "";
      const digits = String(cpf).replace(/\D/g, "");
      if (digits.length < 11) return cpf;
      return `${digits.slice(0, 3)}.***.***-${digits.slice(-2)}`;
    };

    const maskPhone = (phone) => {
      if (!phone) return "";
      const s = String(phone).replace(/\s/g, "");
      if (s.length <= 6) return s;
      return `${s.slice(0, 4)}*****${s.slice(-3)}`;
    };

    const bookingDate = new Date(booking.bookingDate).toLocaleString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    const parseTime = (timeStr) => {
      if (!timeStr || typeof timeStr !== "string") return null;
      const parts = timeStr.split(":").map(Number);
      if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
      const [hours, minutes, seconds = 0] = parts;
      const date = new Date();
      date.setHours(hours, minutes, seconds, 0);
      return date;
    };

    const pickupTimeObj = parseTime(booking.trip_start_time);
    const dropoffTimeObj = parseTime(booking.trip_end_time);

    const pickupTime = pickupTimeObj
      ? pickupTimeObj.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
      : "--:--";

    const dropoffTime = dropoffTimeObj
      ? dropoffTimeObj.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
      : "--:--";

    const formatTime = (seconds) => {
      const s = Number(seconds ?? 0);
      const hours = Math.floor(s / 3600);
      const minutes = Math.floor((s % 3600) / 60);
      const secs = Math.floor(s % 60);
      return `${hours ? `${hours}h ` : ""}${minutes ? `${minutes}m ` : ""}${secs}s`;
    };

    const formattedTripTime = formatTime(booking.total_trip_time);
    const distance = Number(booking.distance ?? 0);
    const distanceUnit = settings?.convert_to_mile ? t("mile") : t("km");

    const safeText = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));

    const waypointsHtml =
      booking?.waypoints?.map(
        (waypoint, index) => `
        <div class="info">
          <label>${t("marker_title_2")} ${index + 1}</label>
          <span class="text">${waypoint.add}</span>
        </div>
      `
      ).join("") || "";

    const vehicleNumber = booking?.vehicle_number ?? booking?.vehicleNumber ?? "";
    // Placa e cor vêm do cadastro do carro (fonte da verdade); o booking é só fallback.
    const plate = driverCar?.placa ?? booking?.placa ?? "";
    const vehicleColor = driverCar?.vehicleColor ?? driverCar?.vehicle_color ?? booking?.vehicleColor ?? booking?.vehicle_color ?? booking?.Vehicle_color ?? "";
    const vehicleModel = driverCar?.vehicleModel ?? booking?.vehicleModel ?? booking?.vehicle_model ?? "";

    const driverPhone =
      booking?.driver_contact ?? booking?.driverPhone ?? driverProfile?.mobile ?? "";

    const driverCPF = booking?.driver_cpf ?? booking?.driverCPF ?? driverProfile?.cpf ?? "";
    const driverEmail = booking?.driver_email ?? booking?.driverEmail ?? driverProfile?.email ?? "";

    const senderName =
      booking?.customer_name ??
      (senderProfile?.firstName ? `${senderProfile?.firstName || ""} ${senderProfile?.lastName || ""}`.trim() : "");

    const senderPhone = booking?.customer_contact ?? senderProfile?.mobile ?? "";
    const senderEmail = booking?.customer_email ?? senderProfile?.email ?? "";
    const senderCPF = booking?.customer_cpf ?? senderProfile?.cpf ?? "";

    // Firebase RTDB pode devolver arrays como objetos indexados; normaliza para array.
    const toArray = (v) =>
      Array.isArray(v) ? v : (v && typeof v === "object" ? Object.values(v) : []);

    const merc = booking?.mercadoriaDetails;
    const mercVolumes = toArray(merc?.volumes);
    const hasMercadoria = !!merc && mercVolumes.length > 0;

    const anexosCell = (v) => {
      const anexos = toArray(v?.anexos);
      if (!anexos.length) return "-";
      return anexos
        .map(
          (a, ai) =>
            `<a href="${a?.url}" target="_blank" rel="noreferrer">${
              a?.name || `${t("attachment") || "Anexo"} ${ai + 1}`
            }</a>`
        )
        .join("<br/>");
    };

    const volumesTableHtml = hasMercadoria
      ? `
      <table class="table">
        <thead>
          <tr>
            <th>${t("product") || "Produto"}</th>
            <th>${t("qty") || "Qtd"}</th>
            <th>${t("invoice") || "NF"}</th>
            <th>${t("weight") || "Peso"}</th>
            <th>${t("value") || "Valor"}</th>
            <th>${t("dimensions") || "Dimensões"}</th>
            <th>${t("attachments") || "Anexos"}</th>
          </tr>
        </thead>
        <tbody>
          ${mercVolumes
            .map((v) => {
              const dims = [v.altura, v.largura, v.profundidade].filter(Boolean).join(" x ");
              return `
                <tr>
                  <td>${safeText(v.produto)}</td>
                  <td>${safeText(v.quantidade)}</td>
                  <td>${safeText(v.notaFiscal)}</td>
                  <td>${safeText(v.peso)}</td>
                  <td>${safeText(v.valor)}</td>
                  <td>${dims || "-"}</td>
                  <td>${anexosCell(v)}</td>
                </tr>
              `;
            })
            .join("")}
        </tbody>
      </table>
      `
      : `<div class="text">${t("no_info_found")}</div>`;

    const mercadoriaHtml = `
      <div class="separator"></div>
      <div class="text">${t("goods_details") || "Detalhes da mercadoria"}</div>
      <div class="small-separator"></div>

      <div class="info"><label>${t("goods_type") || "Tipo"}:</label><span class="text">${safeText(
        booking?.mercadoriaSelected
      )}</span></div>
      <div class="info"><label>${t("total_volumes") || "Total de volumes"}:</label><span class="text">${safeText(
        merc?.totalVolumes
      )}</span></div>
      <div class="info"><label>${t("total_weight") || "Peso total"}:</label><span class="text">${safeText(
        merc?.totalPeso
      )}</span></div>
      <div class="info"><label>${t("total_value") || "Valor total"}:</label><span class="text">${safeText(
        merc?.totalValor
      )}</span></div>

      <div class="small-separator"></div>
      ${volumesTableHtml}
    `;

    const hasRecipient =
      booking?.recipientName ||
      booking?.recipientPhone ||
      booking?.recipientEmail ||
      booking?.recipientCPF ||
      booking?.recipientAddress;

    const recipientHtml = hasRecipient
      ? `
      <div class="separator"></div>
      <div class="text">${t("receiver_info") || "Destinatário"}</div>
      <div class="small-separator"></div>
      <div class="info"><label>${t("name") || "Nome"}:</label><span class="text">${safeText(
        booking?.recipientName
      )}</span></div>
      <div class="info"><label>${t("phone") || "Telefone"}:</label><span class="text">${
        maskPhone(booking?.recipientPhone) || "-"
      }</span></div>
      <div class="info"><label>${t("cpf") || "CPF"}:</label><span class="text">${
        booking?.recipientCPF ? maskCPF(booking.recipientCPF) : "-"
      }</span></div>
      <div class="info"><label>${t("email") || "Email"}:</label><span class="text">${safeText(
        booking?.recipientEmail
      )}</span></div>
      <div class="info"><label>${t("address") || "Endereço"}:</label><span class="text">${safeText(
        booking?.recipientAddress
      )}</span></div>
    `
      : "";

    const hasInstructions = booking?.pickUpInstructions || booking?.deliveryInstructions;
    const instructionsHtml = hasInstructions
      ? `
      <div class="separator"></div>
      <div class="text">${t("instructions") || "Instruções"}</div>
      <div class="small-separator"></div>
      <div class="info"><label>${t("pickUpInstructions") || "Coleta"}:</label><span class="text">${safeText(
        booking?.pickUpInstructions
      )}</span></div>
      <div class="info"><label>${t("deliveryInstructions") || "Entrega"}:</label><span class="text">${safeText(
        booking?.deliveryInstructions
      )}</span></div>
    `
      : "";

    const hasImages = booking?.pickup_image || booking?.deliver_image;
    const imagesHtml = hasImages
      ? `
      <div class="separator"></div>
      <div class="text">${t("proof_images") || "Comprovantes (imagens)"}</div>
      <div class="small-separator"></div>
      <div class="info"><label>${t("pickup_image") || "Foto de coleta"}:</label>
        <span class="text">${
          booking?.pickup_image ? `<a href="${booking.pickup_image}" target="_blank" rel="noreferrer">${t("open") || "Abrir"}</a>` : "-"
        }</span>
      </div>
      <div class="info"><label>${t("deliver_image") || "Foto de entrega"}:</label>
        <span class="text">${
          booking?.deliver_image ? `<a href="${booking.deliver_image}" target="_blank" rel="noreferrer">${t("open") || "Abrir"}</a>` : "-"
        }</span>
      </div>
    `
      : "";

    const normalizeStatus = (raw) => String(raw || "").toUpperCase();
    const statusKey =
    normalizeStatus(booking.status) === "COMPLETED"
        ? "COMPLETE"
        : normalizeStatus(booking.status);

    const statusText = t(statusKey);
    const statusLabel = statusText && statusText !== statusKey ? statusText : statusKey;

    const rtlClass = isRTL ? "rtl" : "";

    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>Booking Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .container { width: 600px; border: 1px solid #ddd; border-radius: 5px; padding: 20px; margin: 0 auto; }
            .header { color: #085c91; font-size: 24px; text-align: center; margin-bottom: 10px; }
            .subheader { color: #333; font-size: 18px; text-align: center; margin-bottom: 10px; }
            .text { color: #000; font-size: 14px; line-height: 1.5; }
            .bold { font-weight: bold; }
            .info { margin-bottom: 5px; display:flex; width:100%; justify-content:space-between; gap: 12px; }
            .info label { color: #085c91; width:30% }
            .info span { width:70% }
            .separator { border-bottom: 2px solid #ddd; margin: 15px 0; }
            .small-separator { border-bottom: 1px solid #ddd; margin: 10px 0; }

            .table { width: 100%; border-collapse: collapse; font-size: 12px; }
            .table th, .table td { border: 1px solid #ddd; padding: 6px; vertical-align: top; }
            .table th { color: #085c91; text-align: left; }

            .rtl { direction: rtl; }
            .rtl .info { flex-direction: row-reverse; }
            .rtl .info label { text-align: right; }
            .rtl .table th { text-align: right; }

            @media print {
              body { padding: 0; }
              .container { border: none; width: 100%; }
              a { color: #000; text-decoration: none; }
            }
          </style>
        </head>
        <body class="${rtlClass}">
          <div class="container">
            <h1 class="header">${settings.appName}</h1>
            <div class="subheader">${t("ride_receipt")}</div>

            <div class="text">Hi <span class="bold">${booking.customer_name || "-"}</span></div>
            <div class="text">${t("ride_greetings")} ${booking.reference || ""}</div>

            <div class="separator"></div>
            <div class="info"><label>${t("status")}:</label><span class="text">${statusLabel}</span></div>
            <div class="info"><label>${t("date")}:</label><span class="text">${bookingDate}</span></div>
            <div class="info"><label class="bold">${t("total")}:</label><span class="text bold">${formatMoney(
              booking.trip_cost
            )}</span></div>

            <div class="separator"></div>

            <div class="text">${t("fare_details")}</div>
            <div class="small-separator"></div>
            ${fareDetails
              .map((item) => `<div class='info'><label>${item.label}:</label><span class='text'>${item.value}</span></div>`)
              .join("")}

            <div class="separator"></div>

            <div class="text">${t("ride_details")}</div>
            <div class="small-separator"></div>
            <div class="info"><label>${t("Discounts")}:</label><span class="text">${formatMoney(
              booking.discount
            )}</span></div>
            <div class="info"><label>${t("Gross_trip_cost")}:</label><span class="text">${formatMoney(
              booking.trip_cost
            )}</span></div>

            <div class="separator"></div>

            <div class="text">${t("payment")}</div>
            <div class="small-separator"></div>
            <div class="info"><span class="text bold">${t(booking.payment_mode)}</span><span class="text bold">${formatMoney(
              booking.trip_cost
            )}</span></div>

            <div class="separator"></div>

            <div class="text">${t("driver_info")}</div>
            <div class="small-separator"></div>
            <div class="info"><label>${t("driver_name")}:</label><span class="text">${booking.driver_name || "-"}</span></div>
            <div class="info"><label>${t("phone")}:</label><span class="text">${maskPhone(driverPhone) || "-"}</span></div>
            <div class="info"><label>${t("cpf")}:</label><span class="text">${
              driverCPF ? maskCPF(driverCPF) : "-"
            }</span></div>
            <div class="info"><label>${t("email")}:</label><span class="text">${safeText(driverEmail)}</span></div>

            <div class="separator"></div>

            <div class="text">${t("vehicle_info")}</div>
            <div class="small-separator"></div>
            <div class="info"><label>${t("vehicle_no")}:</label><span class="text">${vehicleNumber || "-"}</span></div>
            <div class="info"><label>${t("placa")}:</label><span class="text">${plate || "-"}</span></div>
            <div class="info"><label>${t("vehicle_color")}:</label><span class="text">${vehicleColor || "-"}</span></div>
            <div class="info"><label>${t("vehicle_model")}:</label><span class="text">${vehicleModel || "-"}</span></div>

            <div class="separator"></div>

            <div class="text">${t("ride_info")}</div>
            <div class="small-separator"></div>
            <div class="info"><label>${t("marker_title_1")}:</label><span class="text">${pickupTime} | ${
              booking.pickupAddress || "-"
            }</span></div>
            ${waypointsHtml}
            <div class="info"><label>${t("marker_title_2")}:</label><span class="text">${dropoffTime} | ${
              booking.dropAddress || "-"
            }</span></div>
            <div class="info"><label>${t("distance_web")}:</label><span class="text">${distance} ${distanceUnit}</span></div>
            <div class="info"><label>${t("ride_time")}:</label><span class="text">${formattedTripTime}</span></div>

            <div class="separator"></div>

            <div class="text">${t("sender_info")}</div>
            <div class="small-separator"></div>
            <div class="info"><label>${t("name") || "Nome"}:</label><span class="text">${senderName || "-"}</span></div>
            <div class="info"><label>${t("phone") || "Telefone"}:</label><span class="text">${maskPhone(senderPhone) || "-"}</span></div>
            <div class="info"><label>${t("email") || "E-mail"}:</label><span class="text">${safeText(senderEmail)}</span></div>
            <div class="info"><label>${t("cpf") || "CPF"}:</label><span class="text">${
              senderCPF ? maskCPF(senderCPF) : "-"
            }</span></div>

            ${recipientHtml}
            ${mercadoriaHtml}
            ${instructionsHtml}
            ${imagesHtml}

            <div class="separator"></div>
            <div class="text bold" style="display:flex; justify-content:center;">
              ${t("greetings_text")}
            </div>
          </div>
        </body>
      </html>
    `;
  }, [booking, settings, t, i18n, isRTL, fareDetails, driverProfile, senderProfile, driverCar]);

  // dispara print quando html estiver pronto
  useEffect(() => {
    if (!html) return;
    const timer = setTimeout(() => window.print(), 200);
    return () => clearTimeout(timer);
  }, [html]);

  if (!booking || !settings) return null;

  return (
    <div
      // renderiza o HTML “cru” igual no mobile
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default BookingReceiptPrint;